import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PlusCircle, Target, Users, Info, UserPlus, Eye, BookOpen, FileText,
  Video, Mic, Folder, X, ChevronDown
} from 'lucide-react';
import { ComingSoonBanner } from '../ComingSoonBanner';
import EditObjectiveModal from './components/EditObjectiveModal';
import DeleteObjectiveModal from './components/DeleteObjectiveModal';

// Mock previous objectives data
const mockPreviousObjectives = [
  {
    id: 'obj_001',
    employeeName: 'Sarah Johnson',
    department: 'Product Design',
    focus: 'Develop strategic thinking and product vision capabilities for senior leadership transition',
    linkedRole: 'Senior Product Designer',
    horizon: '6 months',
    supporter: 'Michael Chen',
    status: 'Active',
    createdDate: '2024-11-01',
    lastDiscussed: '2024-12-15',
  },
  {
    id: 'obj_002',
    employeeName: 'Sarah Johnson',
    department: 'Product Design',
    focus: 'Build cross-functional collaboration skills and influence without authority',
    linkedRole: 'Senior Product Designer',
    horizon: '3 months',
    supporter: 'Michael Chen',
    status: 'Active',
    createdDate: '2024-10-15',
    lastDiscussed: '2024-12-01',
  },
  {
    id: 'obj_003',
    employeeName: 'Alex Martinez',
    department: 'Product Management',
    focus: 'Strengthen stakeholder management and executive communication skills',
    linkedRole: 'Product Manager',
    horizon: '6 months',
    supporter: 'Michael Chen',
    status: 'Active',
    createdDate: '2024-11-10',
    lastDiscussed: '2024-12-05',
  },
  {
    id: 'obj_004',
    employeeName: 'Sarah Johnson',
    department: 'Product Design',
    focus: 'Master advanced prototyping tools and interactive design techniques',
    linkedRole: 'Product Designer',
    horizon: '6 months',
    supporter: 'David Thompson',
    status: 'Completed',
    createdDate: '2024-01-15',
    lastDiscussed: '2024-06-30',
  },
  {
    id: 'obj_005',
    employeeName: 'Alex Martinez',
    department: 'Product Management',
    focus: 'Develop technical depth and engineering collaboration capabilities',
    linkedRole: 'Product Manager',
    horizon: '3 months',
    supporter: 'Emily Rodriguez',
    status: 'Pending',
    createdDate: '2024-09-20',
    lastDiscussed: null,
  },
];

const mockEmployeeOptions = [
  { value: 'emp_001', label: 'Sarah Johnson - Senior Product Designer' },
  { value: 'emp_002', label: 'Alex Martinez - Product Manager' },
];

const mockEvidenceItems = [
  { id: 'review_001', source: 'Q4 2024 Performance Review', category: 'Strategic Thinking', insight: 'Demonstrated strong strategic thinking in product roadmap discussions.' },
  { id: 'review_002', source: 'Q3 2024 Performance Review', category: 'Collaboration & Communication', insight: 'Excellent cross-functional collaboration with engineering team.' },
  { id: 'coaching_001', theme: 'Executive Presence', frequency: 3, summary: 'Building confidence in presenting to senior leadership.' },
  { id: 'feedback_001', pattern: 'Cross-team Influence', sentiment: 'positive', count: 8, summary: 'Consistently recognized for effective collaboration.' },
];

const getStatusColor = (status: string) => {
  switch (status) {
    case 'Active': return 'bg-green-100 text-green-800';
    case 'Completed': return 'bg-blue-100 text-blue-800';
    case 'Pending': return 'bg-yellow-100 text-yellow-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

const getTypeIcon = (type: string) => {
  const icons: Record<string, typeof BookOpen> = {
    Book: BookOpen,
    Article: FileText,
    YouTube: Video,
    Podcast: Mic,
    Internal: Folder,
  };
  return icons[type] || BookOpen;
};

export default function DevelopmentObjectivesCreator() {
  const { t } = useTranslation();
  const [selectedEmployee, setSelectedEmployee] = useState<string | null>(null);

  // Modal state
  const [showEditObjective, setShowEditObjective] = useState(false);
  const [showDeleteObjective, setShowDeleteObjective] = useState(false);
  const [selectedObjective, setSelectedObjective] = useState<any>(null);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <ComingSoonBanner
        icon={<PlusCircle className="w-6 h-6" />}
        title="Development Objectives Creator"
        description="Development Objectives Creator is currently in development. This is a preview of the upcoming feature."
        gradientFrom="from-blue-500"
        gradientTo="to-indigo-500"
      />

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-start space-x-4">
            <div className="w-14 h-14 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
              <Target className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">{t('Development Objectives')}</h1>
              <p className="text-muted-foreground text-base">
                {t('Design personalized development objectives based on evidence and career aspirations')}
              </p>
            </div>
          </div>
        </div>

        {/* Design Principles Notice */}
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <Info className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-foreground mb-1">{t('Design Principles')}</p>
              <p className="text-sm text-muted-foreground">
                {t('Focus on "why this exists" over "how to measure". No numeric ratings, competency matrices, or scoring. Emphasize continuity and growth over outcomes.')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Employee Selection */}
      <div className="mb-8">
        <div className="bg-card border border-border rounded-xl p-6">
          <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center space-x-2">
            <Users className="w-5 h-5 text-primary" />
            <span>{t('Select Team Member')}</span>
          </h2>
          <div className="relative">
            <select
              className="w-full px-4 py-3 border border-border rounded-lg bg-muted text-muted-foreground appearance-none"
             
            >
              <option>{t('Choose an employee to create development objective for...')}</option>
              {mockEmployeeOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <ChevronDown className="w-4 h-4 text-muted-foreground absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>
        </div>
      </div>

      {/* Placeholder when no employee selected */}
      <div className="text-center py-16 mb-8">
        <div className="w-20 h-20 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <UserPlus className="w-8 h-8 text-primary" />
        </div>
        <h3 className="text-lg font-semibold text-foreground mb-2">{t('Select an Employee to Begin')}</h3>
        <p className="text-muted-foreground">{t('Choose a team member to create a development objective for them')}</p>
      </div>

      {/* Form Preview (shown as static content) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* Left Column - Objective Form */}
        <div className="space-y-6">
          <div className="bg-card border border-border rounded-xl p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center space-x-2">
              <Target className="w-5 h-5 text-primary" />
              <span>{t('Objective Details')}</span>
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1">{t('Development Focus')}</label>
                <textarea
                  rows={3}
                  placeholder={t('What capability or growth area should this person develop?')}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-muted text-muted-foreground resize-none"
                 
                />
                <p className="text-xs text-muted-foreground mt-1">0 / 120 {t('characters')}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1">{t('Development Horizon')}</label>
                <select className="w-full px-3 py-2 border border-border rounded-lg bg-muted text-muted-foreground">
                  <option>3 months</option>
                  <option>6 months</option>
                  <option>12 months</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1">{t('Primary Supporter')}</label>
                <input
                  type="text"
                  value="Michael Chen (You)"
                  className="w-full px-3 py-2 border border-border rounded-lg bg-muted text-muted-foreground"
                 
                />
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Evidence Selection */}
        <div className="space-y-6">
          <div className="bg-card border border-border rounded-xl p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">{t('Supporting Evidence')}</h3>
            <div className="space-y-3">
              {mockEvidenceItems.map((item) => (
                <div key={item.id} className="p-3 border border-border rounded-lg ">
                  <div className="flex items-start space-x-3">
                    <input type="checkbox" className="mt-1" />
                    <div>
                      <p className="text-sm font-medium text-foreground">{'source' in item ? item.source : ('theme' in item ? item.theme : item.pattern)}</p>
                      <p className="text-xs text-muted-foreground mt-1">{'insight' in item ? item.insight : item.summary}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Learning Sources */}
          <div className="bg-card border border-border rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">{t('Learning Sources')}</h3>
              <button
                className="flex items-center space-x-1 px-3 py-1.5 text-xs bg-primary text-white rounded-lg"
               
              >
                <PlusCircle className="w-3 h-3" />
                <span>{t('Add Source')}</span>
              </button>
            </div>
            <p className="text-sm text-muted-foreground">{t('Add up to 3 learning resources (books, articles, videos)')}</p>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-end space-x-4">
        <button
          className="px-4 py-2 border border-border text-muted-foreground rounded-lg"
         
        >
          {t('Cancel')}
        </button>
        <button
          className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-lg"
         
        >
          <Eye className="w-4 h-4" />
          <span>{t('Preview Objective')}</span>
        </button>
      </div>

      {/* Previous Objectives Section */}
      <div className="mt-12">
        <h2 className="text-xl font-semibold text-foreground mb-6 flex items-center space-x-2">
          <Target className="w-5 h-5 text-primary" />
          <span>{t('Previously Created Objectives')}</span>
        </h2>
        <div className="space-y-4">
          {mockPreviousObjectives.map((obj) => (
            <div key={obj.id} className="bg-card border border-border rounded-xl p-5">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-sm font-semibold text-foreground">{obj.focus}</h3>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(obj.status)}`}>
                      {t(obj.status)}
                    </span>
                  </div>
                  <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                    <span>{obj.employeeName}</span>
                    <span>|</span>
                    <span>{obj.linkedRole}</span>
                    <span>|</span>
                    <span>{t('Horizon')}: {obj.horizon}</span>
                    <span>|</span>
                    <span>{t('Supporter')}: {obj.supporter}</span>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    className="px-3 py-1.5 text-xs border border-border text-muted-foreground rounded-lg"
                    onClick={() => { setSelectedObjective(obj); setShowEditObjective(true); }}
                  >
                    {t('Edit')}
                  </button>
                  <button
                    className="px-3 py-1.5 text-xs border border-red-200 text-red-600 rounded-lg"
                    onClick={() => { setSelectedObjective(obj); setShowDeleteObjective(true); }}
                  >
                    {t('Delete')}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modals */}
      <EditObjectiveModal
        isOpen={showEditObjective}
        onClose={() => { setShowEditObjective(false); setSelectedObjective(null); }}
        onSave={(data) => {
          console.log('Edit objective:', data);
          setShowEditObjective(false);
          setSelectedObjective(null);
        }}
        objective={selectedObjective}
      />

      <DeleteObjectiveModal
        isOpen={showDeleteObjective}
        onClose={() => { setShowDeleteObjective(false); setSelectedObjective(null); }}
        onDelete={(id) => {
          console.log('Delete objective:', id);
          setShowDeleteObjective(false);
          setSelectedObjective(null);
        }}
        objective={selectedObjective}
      />
    </div>
  );
}
